import { type ComponentRef, forwardRef } from "react";
import styled from "styled-components";
import {
	PlateElement,
	useEditorPlugin,
	type PlateElementProps,
} from "@platejs/core/react";
import type { TTableCellElement } from "@platejs/utils";
import {
	TablePlugin,
	useTableCellElement,
	useTableCellElementResizable,
} from "@platejs/table/react";

interface TableCellElementProps extends PlateElementProps<TTableCellElement> {
	hideBorder?: boolean;
	isHeader?: boolean;
}

export const TableCellElement = forwardRef<
	ComponentRef<typeof PlateElement>,
	TableCellElementProps
>(({ children, className, style, hideBorder, isHeader, ...props }, ref) => {
	const { colIndex, rowIndex, colSpan, minHeight } = useTableCellElement();
	const { api } = useEditorPlugin(TablePlugin);
	const { leftProps: _, rightProps: __ } = useTableCellElementResizable({
		colSpan,
		colIndex,
		rowIndex,
	});

	return (
		<PlateElement 
			ref={ref}
			{...props}
			attributes={{
				...props.attributes,
				colSpan: api.table.getColSpan(props.element),
				rowSpan: api.table.getRowSpan(props.element),
			}}
		>
			<TableElement as={isHeader ? "th" : "td"}>
				<div style={{ minHeight }}>{children}</div>
			</TableElement>
		</PlateElement>

	);
});

const TableElement = styled.td`
	&& {
		border: 1px solid rgb(193, 199, 208);
		min-width: 3rem;
		padding: 0.5rem;
		text-align: left;
	
	${props => {
		if (props.as !== "th") return;

		return "background-color: rgb(244, 245, 247);";
	}}

	> * {
		margin: 0;
	}
}
`;

TableCellElement.displayName = "TableCellElement";

export const TableCellHeaderElement = forwardRef<
	ComponentRef<typeof TableCellElement>,
	TableCellElementProps
>((props, ref) => <TableCellElement ref={ref} {...props} isHeader />);

TableCellHeaderElement.displayName = "TableCellHeaderElement";
